import { Button } from '../components/ui/button';
import { ExternalLink, ShoppingBag, Download, BookOpen, Video, FileText } from 'lucide-react';

const STAN_STORE = "https://stan.store/straight4thetop";
const GUMROAD = "https://straight4thetop.gumroad.com";

const stanStoreItems = [
  {
    icon: Video,
    title: '1-on-1 Coaching Call',
    description: 'Personalized coaching session to discuss your real estate goals and strategy.'
  },
  {
    icon: BookOpen,
    title: 'Real Estate Mentorship',
    description: 'Comprehensive mentorship program for new and experienced agents.'
  },
  {
    icon: FileText,
    title: 'Business Consultation',
    description: 'Strategic consultation for your real estate business growth.'
  }
];

const gumroadItems = [
  {
    icon: Download,
    title: 'Buyer\'s Guide',
    description: 'Complete guide for first-time home buyers with checklists and tips.'
  },
  {
    icon: Download,
    title: 'Seller\'s Playbook',
    description: 'Step-by-step guide to selling your home for maximum value.'
  },
  {
    icon: Download,
    title: 'Investor\'s Toolkit',
    description: 'Analysis templates and guides for real estate investors.'
  }
];

export const Resources = () => {
  return (
    <div data-testid="resources-page">
      {/* Hero Section */}
      <section className="py-24 bg-[#0a0a0a]" data-testid="resources-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <p className="section-overline">Resources</p>
            <h1 className="text-4xl sm:text-5xl font-light text-[#F5F5F0] mt-4 mb-6" style={{ fontFamily: 'Playfair Display' }}>
              Your Real Estate <span className="text-gold-gradient">Toolkit</span>
            </h1>
            <p className="text-lg text-[#808080] max-w-2xl mx-auto">
              Access premium resources, training materials, and personalized services to help you succeed in real estate.
            </p>
          </div>
        </div>
      </section>

      {/* Stan Store Section */}
      <section className="py-24 bg-[#111111]" data-testid="stan-store-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row items-start justify-between mb-12">
            <div>
              <p className="section-overline">Services & Booking</p>
              <h2 className="section-title mt-2">Work With Me</h2>
              <p className="text-[#808080] mt-4 max-w-xl">
                Book personalized coaching sessions, consultations, and services to accelerate your real estate journey.
              </p>
            </div>
            <a href={STAN_STORE} target="_blank" rel="noopener noreferrer" className="mt-6 lg:mt-0">
              <Button className="btn-gold" data-testid="stan-store-main-btn">
                <ShoppingBag className="w-4 h-4 mr-2" /> Visit Stan Store <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {stanStoreItems.map((item, index) => (
              <div key={index} className="card-hover bg-[#0a0a0a] p-6 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-light text-[#F5F5F0] mb-3" style={{ fontFamily: 'Playfair Display' }}>
                  {item.title}
                </h3>
                <p className="text-[#808080] text-sm mb-4">{item.description}</p>
                <a href={STAN_STORE} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="btn-outline-gold w-full">
                    Book Now <ExternalLink className="w-3 h-3 ml-2" />
                  </Button>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gumroad Section */}
      <section className="py-24 bg-[#0a0a0a]" data-testid="gumroad-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row items-start justify-between mb-12">
            <div>
              <p className="section-overline">Digital Products</p>
              <h2 className="section-title mt-2">Download Resources</h2>
              <p className="text-[#808080] mt-4 max-w-xl">
                Get instant access to guides, templates, and resources to help you buy, sell, or invest smarter.
              </p>
            </div>
            <a href={GUMROAD} target="_blank" rel="noopener noreferrer" className="mt-6 lg:mt-0">
              <Button className="btn-gold" data-testid="gumroad-main-btn">
                <Download className="w-4 h-4 mr-2" /> Visit Gumroad <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {gumroadItems.map((item, index) => (
              <div key={index} className="card-hover bg-[#111111] p-6 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-light text-[#F5F5F0] mb-3" style={{ fontFamily: 'Playfair Display' }}>
                  {item.title}
                </h3>
                <p className="text-[#808080] text-sm mb-4">{item.description}</p>
                <a href={GUMROAD} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="btn-outline-gold w-full">
                    Download <ExternalLink className="w-3 h-3 ml-2" />
                  </Button>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#111111]" data-testid="resources-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="section-title mb-4">Need Something Specific?</h2>
          <p className="text-[#808080] mb-8 max-w-xl mx-auto">
            Can't find what you're looking for? Let's chat about your specific needs and create a custom solution.
          </p>
          <a href="https://calendly.com/seantherealtor/15min" target="_blank" rel="noopener noreferrer">
            <Button className="btn-gold" data-testid="resources-calendly-btn">
              Schedule a Call <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};
